import React from 'react';

const Overview = () => {
  return (
    <div className="doc-content">
      <div className="quickstart-header">
        <p className="quickstart-category">Introduction</p>
        <h1>Overview</h1>
      </div>
      
      <div className="info-banner">
        <i className="fa-solid fa-info-circle"></i>
        <div>
          <p>MemU is the next-generation agentic memory framework for AI applications with 92% accuracy and up to 90% cost reduction. Our agentic architecture enables autonomous memory management and evolution.</p>
        </div>
      </div>
      
      <p><strong>MemU</strong> is an open-source agentic memory framework for AI applications—high accuracy, fast retrieval, low cost. It acts as an intelligent "memory folder" powered by autonomous agents that adapt to different scenarios across various AI use cases.</p>
      
      <p>With <strong>MemU's agentic architecture</strong>, you can build AI applications that truly remember their users through autonomous memory management. They learn preferences, context, and patterns, growing smarter with every interaction through agentic memory evolution.</p>
      
      <h2>What is Agentic Memory?</h2>
      <p>Agentic memory represents a revolutionary approach to AI memory systems where autonomous agents manage, organize, and evolve memory structures without human intervention. Unlike traditional static memory systems, agentic memory:</p>
      
      <ul>
        <li><strong>Autonomously decides</strong> what information to store, update, or archive</li>
        <li><strong>Self-organizes</strong> memory structures based on usage patterns and relationships</li>
        <li><strong>Proactively creates</strong> connections between related memories</li>
        <li><strong>Continuously evolves</strong> through self-reflection and pattern recognition</li>
        <li><strong>Adapts memory strategies</strong> based on application context and user behavior</li>
      </ul>
      
      <h2>MemU vs. Traditional Memory Systems</h2>
      <p>Different memory approaches serve different purposes. Here's how MemU's agentic memory architecture compares to traditional alternatives:</p>

      <div className="comparison-table">
        <table>
          <thead>
            <tr>
              <th>Feature</th>
              <th>Traditional Memory Systems</th>
              <th>MemU (Agentic Memory)</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td><strong>Structure</strong></td>
              <td>Fragmented data</td>
              <td>Documents/Videos/Images (File system)</td>
            </tr>
            <tr>
              <td><strong>Memory Format</strong></td>
              <td>LLM inference</td>
              <td>Autonomous Agent-managed</td>
            </tr>
            <tr>
              <td><strong>Retrieval Method</strong></td>
              <td>Embedding Search</td>
              <td>Embedding Search + Document Search</td>
            </tr>
            <tr>
              <td><strong>Knowledge Base Format</strong></td>
              <td>Tree/Graph network</td>
              <td>Inter-document hyperlinks</td>
            </tr>
            <tr>
              <td><strong>Self-Evolving</strong></td>
              <td>No</td>
              <td>Yes</td>
            </tr>
            <tr>
              <td><strong>Forgetting Mechanism</strong></td>
              <td>No</td>
              <td>Yes</td>
            </tr>
          </tbody>
        </table>
      </div>

             <h2>Agentic Memory as a File System</h2>
       <p>MemU features an advanced Agentic Memory Management System that revolutionizes how AI applications handle and retain information. Our autonomous agents act as intelligent file system managers, organizing and evolving memories without human intervention.</p>

       <div className="platform-cards">
         <div className="platform-card">
           <div className="card-icon">
             <i className="fa-solid fa-folder-tree"></i>
           </div>
           <h3>Organize</h3>
           <h4>Agentic Memory Organization</h4>
           <p>Your memories are structured as intelligent folders managed by dedicated memory agents. These agentic systems automatically decide what to record, modify, or archive based on relevance, usage patterns, and contextual importance through autonomous decision-making.</p>
         </div>
         
         <div className="platform-card">
           <div className="card-icon">
             <i className="fa-solid fa-network-wired"></i>
           </div>
           <h3>Link</h3>
           <h4>Agentic Memory Interconnections</h4>
           <p>Memories don't exist in isolation in our agentic architecture. Memory agents automatically discover and create meaningful connections between related memories, building a rich network of hyperlinked documents through autonomous relationship mapping.</p>
         </div>
       </div>

       <div className="platform-cards">
         <div className="platform-card">
           <div className="card-icon">
             <i className="fa-solid fa-brain"></i>
           </div>
           <h3>Evolve</h3>
           <h4>Agentic Memory Evolution</h4>
           <p>Even when offline, your agentic memory system keeps working autonomously. Memory agents generate new insights by analyzing existing memories, identify patterns, and create summary documents through self-reflection and autonomous learning processes.</p>
         </div>
         
         <div className="platform-card">
           <div className="card-icon">
             <i className="fa-solid fa-shield-heart"></i>
           </div>
           <h3>Never Forget</h3>
           <h4>Agentic Memory Retention</h4>
           <p>We've revolutionized memory persistence with agentic importance algorithms. Memory agents dynamically assess and adjust memory priorities, ensuring that what matters most stays accessible through autonomous memory management strategies.</p>
         </div>
       </div>


    </div>
  );
};

export default Overview;